import { Threebox } from 'threebox-plugin';
import * as THREE from 'three';
import buildingModelsService from '../services/buildingModelsService.js';

/**
 * Threebox integration utilities for 3D models in Mapbox
 */
export class ThreeboxManager {
  constructor() {
    this.threebox = null;
    this.map = null;
    this.loadedModels = new Map(); // Track loaded 3D objects
    this.isInitialized = false;
  }

  /**
   * Initialize Threebox with map instance
   */
  initialize(map) {
    if (this.isInitialized) return;
    
    this.map = map;
    this.threebox = new Threebox(
      map,
      map.getCanvas().getContext('webgl'),
      {
        defaultLights: true,
        enableSelectingObjects: true,
        enableDraggingObjects: false,
        enableRotatingObjects: false,
        enableTooltips: true
      }
    );

    // Fix for "tb is not defined" error - make threebox globally accessible
    window.tb = this.threebox;

    this.isInitialized = true;
    console.log('Threebox initialized successfully');
  }

  /**
   * Add 3D building models layer to map
   */
  addBuildingModelsLayer() {
    if (!this.map || !this.threebox) {
      console.error('Map or Threebox not initialized');
      return;
    }

    // Add custom 3D layer
    this.map.addLayer({
      id: '3d-buildings-models',
      type: 'custom',
      renderingMode: '3d',
      onAdd: () => {
        console.log('3D buildings models layer added');
        this.loadVisibleBuildings();
      },
      render: () => {
        this.threebox.update();
      }
    });

    // Add event listeners for map movement
    this.map.on('moveend', () => this.loadVisibleBuildings());
    this.map.on('zoomend', () => this.loadVisibleBuildings());
  }

  /**
   * Load buildings visible in current map view
   */
  async loadVisibleBuildings() {
    if (!this.map || !this.threebox) return;

    const bounds = this.map.getBounds();
    const zoom = this.map.getZoom();
    
    // Get buildings that should be visible
    const visibleBuildings = buildingModelsService.getBuildingsInBounds(bounds, zoom);
    
    // Remove models that are no longer visible
    this.removeInvisibleModels(visibleBuildings);
    
    // Add new models
    for (const building of visibleBuildings) {
      if (!this.loadedModels.has(building.id)) {
        await this.loadBuildingModel(building);
      }
    }
  }

  /**
   * Load a single building model
   */
  async loadBuildingModel(building) {
    try {
      console.log(`Loading 3D model for ${building.name}`);

      // Ensure threebox is initialized
      if (!this.threebox) {
        throw new Error('Threebox not initialized');
      }

      // Check if model file exists
      const modelExists = await buildingModelsService.checkModelExists(building.modelUrl);
      
      // Load the 3D model using threebox loadObj method
      const fileType = building.modelUrl.endsWith('.glb') ? 'gltf' : 'gltf'; // GLB uses gltf loader
      const options = {
        obj: building.modelUrl,
        type: fileType,
        scale: building.scale || { x: 1, y: 1, z: 1 },
        units: 'meters',
        rotation: building.rotation || { x: 0, y: 0, z: 0 }
      };

      console.log('Loading 3D model with threebox...', options);
      
      // Use threebox loadObj to load the model directly
      this.threebox.loadObj(options, (threeboxObject) => {
        if (!threeboxObject) {
          throw new Error('Failed to load model with threebox');
        }

        console.log('Threebox model loaded successfully:', threeboxObject);

        // Position the model
        threeboxObject.setCoords(building.coordinates);

        // Add to scene
        this.threebox.add(threeboxObject);

        // Store reference
        this.loadedModels.set(building.id, {
          building,
          threeboxObject,
          model: threeboxObject
        });

        // Add click handler
        this.addBuildingClickHandler(threeboxObject, building);

        console.log(`Successfully loaded 3D model for ${building.name}`);
      });

      // Handle fallback for missing models
      if (!modelExists) {
        console.warn(`Model file not found: ${building.modelUrl}, using fallback`);
        // Use fallback model - create a simple Three.js object
        const fallbackModel = buildingModelsService.createFallbackModel();
        const threeboxObject = this.threebox.Object3D({
          obj: fallbackModel,
          units: 'meters'
        });
        threeboxObject.setCoords(building.coordinates);
        this.threebox.add(threeboxObject);
        
        this.loadedModels.set(building.id, {
          building,
          threeboxObject,
          model: fallbackModel
        });

        console.log(`Loaded fallback model for ${building.name}`);
      }

    } catch (error) {
      console.error(`Failed to load building model for ${building.name}:`, error);
      
      // Try fallback model
      try {
        const fallbackModel = buildingModelsService.createFallbackModel();
        const threeboxObject = this.threebox.Object3D({
          obj: fallbackModel,
          units: 'meters'
        });
        threeboxObject.setCoords(building.coordinates);
        this.threebox.add(threeboxObject);
        
        this.loadedModels.set(building.id, {
          building,
          threeboxObject,
          model: fallbackModel
        });

        console.log(`Loaded fallback model for ${building.name}`);
      } catch (fallbackError) {
        console.error(`Failed to load fallback model for ${building.name}:`, fallbackError);
      }
    }
  }

  /**
   * Remove models that are no longer visible
   */
  removeInvisibleModels(visibleBuildings) {
    const visibleIds = new Set(visibleBuildings.map(b => b.id));
    
    for (const [buildingId, modelData] of this.loadedModels.entries()) {
      if (!visibleIds.has(buildingId)) {
        console.log(`Removing 3D model for ${modelData.building.name}`);
        this.threebox.remove(modelData.threeboxObject);
        this.loadedModels.delete(buildingId);
      }
    }
  }

  /**
   * Add click handler to building model
   */
  addBuildingClickHandler(threeboxObject, building) {
    // This would integrate with your existing place info system
    threeboxObject.addEventListener('SelectedChange', (e) => {
      if (e.detail.selected) {
        console.log(`Clicked on ${building.name}`);
        
        // You can emit an event to open the info modal
        window.dispatchEvent(new CustomEvent('buildingClicked', {
          detail: {
            building,
            coordinates: building.coordinates,
            name: building.name,
            country: building.country,
            city: building.city
          }
        }));
      }
    });
  }

  /**
   * Highlight a specific building (for when user searches/navigates to it)
   */
  highlightBuilding(buildingId) {
    const modelData = this.loadedModels.get(buildingId);
    if (modelData) {
      // Add highlight effect
      const highlightMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.3
      });
      
      // This is a simplified highlight - you'd want to make it more sophisticated
      modelData.model.traverse((child) => {
        if (child.isMesh) {
          child.material = highlightMaterial;
        }
      });

      // Remove highlight after 3 seconds
      setTimeout(() => {
        this.removeHighlight(buildingId);
      }, 3000);
    }
  }

  /**
   * Remove highlight from building
   */
  removeHighlight(buildingId) {
    const modelData = this.loadedModels.get(buildingId);
    if (modelData) {
      // Restore original materials
      // This would need to be implemented based on your model structure
      console.log(`Removing highlight from ${modelData.building.name}`);
    }
  }

  /**
   * Get building at specific coordinates (for programmatic access)
   */
  getBuildingAt(lng, lat) {
    return buildingModelsService.getBuildingAt(lng, lat);
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this.threebox) {
      // Remove all objects
      for (const [, modelData] of this.loadedModels.entries()) {
        this.threebox.remove(modelData.threeboxObject);
      }
      this.loadedModels.clear();
      
      // Clean up threebox
      this.threebox.clear();
    }

    // Clear service cache
    buildingModelsService.clearCache();
    
    this.isInitialized = false;
    console.log('Threebox manager disposed');
  }
}

export default new ThreeboxManager();